import React, { useMemo, useState } from 'react';
import { Empty, List, Segmented, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { ProductQuestionItem } from '../../../api/ProductQuestions';

const { Text, Paragraph } = Typography;

type Props = {
  article: string;
  items: ProductQuestionItem[];
  total: number;
  pending?: boolean;
};

export const QuestionsTab = ({ article, items, total, pending }: Props) => {
  const [filter, setFilter] = useState<'all' | 'new'>('all');
  const [mp, setMp] = useState<string | 'all'>('all');

  const marketplaces = useMemo(
    () => [...new Set(items.map((i) => i.marketplace).filter(Boolean))],
    [items],
  );

  const filtered = items.filter((i) => {
    if (mp !== 'all' && i.marketplace !== mp) return false;
    if (filter === 'new') {
      return (
        !i.is_processed &&
        (i.workflow_status === 'NEW' || i.workflow_status === 'WAITING')
      );
    }
    return true;
  });

  return (
    <div>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap>
          <Segmented
            value={filter}
            onChange={(v) => setFilter(v as 'all' | 'new')}
            options={[
              { label: 'Все', value: 'all' },
              { label: 'Новые', value: 'new' },
            ]}
          />
          <Segmented
            value={mp}
            onChange={(v) => setMp(v as string)}
            options={[
              { label: 'Все МП', value: 'all' },
              ...marketplaces.map((m) => ({ label: m, value: m })),
            ]}
          />
        </Space>
        <Link to={`/questions?article=${encodeURIComponent(article)}`}>
          Открыть очередь вопросов →
        </Link>
      </Space>

      {pending ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !filtered.length ? (
        <Empty description="Нет вопросов" />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={filtered.slice(0, 20)}
          footer={
            total > filtered.length ? (
              <Text type="secondary">Всего: {total}</Text>
            ) : null
          }
          renderItem={(item) => (
            <List.Item>
              <Space wrap size={[8, 4]}>
                <Tag>{item.marketplace}</Tag>
                <Tag
                  color={
                    item.workflow_status === 'NEW' ? 'warning' : 'default'
                  }
                >
                  {item.workflow_status}
                </Tag>
                <Text type="secondary">
                  {item.asked_at
                    ? new Date(item.asked_at).toLocaleDateString('ru')
                    : ''}
                </Text>
              </Space>
              <Paragraph style={{ margin: '8px 0 0' }} ellipsis={{ rows: 3 }}>
                {item.question_text || 'Без текста'}
              </Paragraph>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
